import type { DbExecutor } from "../executor";
import { generateId } from "../ids";
import {
  validateObservation,
  validateProduct,
  validateSupplier,
  type MaterialDefinition,
  type OfferAvailability,
  type PriceObservation,
  type PriceSourceType,
  type Product,
  type ProductAttribute,
  type ProductStatus,
  type ProjectProductSelection,
  type Supplier,
  type SupplierAccountType,
} from "../../domain/catalog";
import type { QuantityUnit } from "../../domain/feature";
import { ValidationError } from "./projectRepository";

interface SupplierRow { id: string; name: string; account_type: string; website: string; notes: string; archived_at: string | null; created_at: string; updated_at: string }
interface DefinitionRow { id: string; name: string; construction_unit: string; category_id: string | null; notes: string; is_system: number; sort_order: number; archived_at: string | null }
interface ProductRow { id: string; material_definition_id: string; name: string; brand: string; sku: string; supplier_id: string | null; purchase_unit: string; coverage_per_unit_x100: number; coverage_unit: string; product_url: string; status: string; notes: string; created_at: string; updated_at: string }
interface ObservationRow { id: string; product_id: string; supplier_id: string | null; price_cents: number; price_includes_tax: number; source_type: string; observed_at: string; valid_until: string | null; context_note: string; notes: string; market_zip: string | null; availability: string; offer_url: string; supplier_sku: string; provider_id: string | null; created_at: string }
interface SelectionRow { id: string; component_id: string; product_id: string; price_observation_id: string | null; product_name_snapshot: string; supplier_name_snapshot: string; purchase_unit_snapshot: string; coverage_per_unit_x100_snap: number; coverage_unit_snapshot: string; price_cents_snapshot: number; price_includes_tax_snapshot: number; price_source_snapshot: string; price_observed_at_snapshot: string | null; round_separately: number; purchase_qty_override: number | null; created_at: string; replaced_at: string | null }

const mapSupplier = (r: SupplierRow): Supplier => ({ id: r.id, name: r.name, accountType: r.account_type as SupplierAccountType, website: r.website, notes: r.notes, archivedAt: r.archived_at, createdAt: r.created_at, updatedAt: r.updated_at });
const mapDefinition = (r: DefinitionRow): MaterialDefinition => ({ id: r.id, name: r.name, constructionUnit: r.construction_unit as QuantityUnit, categoryId: r.category_id, notes: r.notes, isSystem: Number(r.is_system) === 1, sortOrder: Number(r.sort_order), archivedAt: r.archived_at });
const mapProduct = (r: ProductRow): Product => ({ id: r.id, materialDefinitionId: r.material_definition_id, name: r.name, brand: r.brand, sku: r.sku, supplierId: r.supplier_id, purchaseUnit: r.purchase_unit as QuantityUnit, coveragePerUnitX100: Number(r.coverage_per_unit_x100), coverageUnit: r.coverage_unit as QuantityUnit, productUrl: r.product_url, status: r.status as ProductStatus, notes: r.notes, createdAt: r.created_at, updatedAt: r.updated_at });
const mapObservation = (r: ObservationRow): PriceObservation => ({ id: r.id, productId: r.product_id, supplierId: r.supplier_id, priceCents: Number(r.price_cents), priceIncludesTax: Number(r.price_includes_tax) === 1, sourceType: r.source_type as PriceSourceType, observedAt: r.observed_at, validUntil: r.valid_until, contextNote: r.context_note, notes: r.notes, marketZip: r.market_zip ?? null, availability: (r.availability ?? "unknown") as OfferAvailability, offerUrl: r.offer_url ?? "", supplierSku: r.supplier_sku ?? "", providerId: r.provider_id ?? null, createdAt: r.created_at });
const mapSelection = (r: SelectionRow): ProjectProductSelection => ({ id: r.id, componentId: r.component_id, productId: r.product_id, priceObservationId: r.price_observation_id, productNameSnapshot: r.product_name_snapshot, supplierNameSnapshot: r.supplier_name_snapshot, purchaseUnitSnapshot: r.purchase_unit_snapshot as QuantityUnit, coveragePerUnitX100Snapshot: Number(r.coverage_per_unit_x100_snap), coverageUnitSnapshot: r.coverage_unit_snapshot as QuantityUnit, priceCentsSnapshot: Number(r.price_cents_snapshot), priceIncludesTaxSnapshot: Number(r.price_includes_tax_snapshot) === 1, priceSourceSnapshot: r.price_source_snapshot as PriceSourceType, priceObservedAtSnapshot: r.price_observed_at_snapshot, roundSeparately: Number(r.round_separately) === 1, purchaseQtyOverride: r.purchase_qty_override === null ? null : Number(r.purchase_qty_override), createdAt: r.created_at, replacedAt: r.replaced_at });

export interface ProductInput {
  materialDefinitionId: string;
  name: string;
  brand: string;
  sku: string;
  supplierId: string | null;
  purchaseUnit: QuantityUnit;
  coveragePerUnitX100: number;
  coverageUnit: QuantityUnit;
  productUrl: string;
  notes: string;
}

export interface ObservationInput {
  supplierId: string | null;
  priceCents: number;
  priceIncludesTax: boolean;
  sourceType: PriceSourceType;
  observedAt: string;
  validUntil: string | null;
  contextNote: string;
  notes: string;
  /** Offer fields (Phase 9B). Optional so existing callers stay valid. */
  marketZip?: string | null;
  availability?: OfferAvailability;
  offerUrl?: string;
  supplierSku?: string;
  /** Set ONLY by a real provider import; manual entry must leave it null. */
  providerId?: string | null;
}

export class CatalogRepository {
  constructor(private readonly db: DbExecutor) {}

  /* ---------------- Suppliers ---------------- */

  async listSuppliers(): Promise<Supplier[]> {
    return (await this.db.select<SupplierRow>("SELECT * FROM suppliers ORDER BY name")).map(mapSupplier);
  }

  async createSupplier(input: { name: string; accountType: SupplierAccountType; website: string; notes: string }): Promise<Supplier> {
    const problems = validateSupplier(input.name);
    if (problems.length > 0) throw new ValidationError(problems);
    const now = new Date().toISOString();
    const s: Supplier = { id: generateId(), name: input.name.trim(), accountType: input.accountType, website: input.website.trim(), notes: input.notes, archivedAt: null, createdAt: now, updatedAt: now };
    await this.db.execute(
      "INSERT INTO suppliers (id, name, account_type, website, notes, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?, ?)",
      [s.id, s.name, s.accountType, s.website, s.notes, s.createdAt, s.updatedAt],
    );
    return s;
  }

  async updateSupplier(id: string, input: { name: string; accountType: SupplierAccountType; website: string; notes: string }): Promise<void> {
    const problems = validateSupplier(input.name);
    if (problems.length > 0) throw new ValidationError(problems);
    await this.db.execute("UPDATE suppliers SET name = ?, account_type = ?, website = ?, notes = ?, updated_at = ? WHERE id = ?",
      [input.name.trim(), input.accountType, input.website.trim(), input.notes, new Date().toISOString(), id]);
  }

  async archiveSupplier(id: string): Promise<void> {
    await this.db.execute("UPDATE suppliers SET archived_at = ?, updated_at = ? WHERE id = ? AND archived_at IS NULL", [new Date().toISOString(), new Date().toISOString(), id]);
  }

  async unarchiveSupplier(id: string): Promise<void> {
    await this.db.execute("UPDATE suppliers SET archived_at = NULL, updated_at = ? WHERE id = ?", [new Date().toISOString(), id]);
  }

  /* ---------------- Material definitions ---------------- */

  async listDefinitions(): Promise<MaterialDefinition[]> {
    return (await this.db.select<DefinitionRow>("SELECT * FROM material_definitions ORDER BY sort_order, name")).map(mapDefinition);
  }

  async createDefinition(input: { name: string; constructionUnit: QuantityUnit; categoryId: string | null; notes: string }): Promise<MaterialDefinition> {
    if (input.name.trim() === "") throw new ValidationError(["Material name is required."]);
    const existing = await this.listDefinitions();
    const d: MaterialDefinition = { id: generateId(), name: input.name.trim(), constructionUnit: input.constructionUnit, categoryId: input.categoryId, notes: input.notes, isSystem: false, sortOrder: existing.reduce((m, x) => Math.max(m, x.sortOrder), 0) + 10, archivedAt: null };
    await this.db.execute("INSERT INTO material_definitions (id, name, construction_unit, category_id, notes, is_system, sort_order) VALUES (?, ?, ?, ?, ?, 0, ?)",
      [d.id, d.name, d.constructionUnit, d.categoryId, d.notes, d.sortOrder]);
    return d;
  }

  async renameDefinition(id: string, name: string): Promise<void> {
    if (name.trim() === "") throw new ValidationError(["Material name is required."]);
    await this.db.execute("UPDATE material_definitions SET name = ? WHERE id = ?", [name.trim(), id]);
  }

  async archiveDefinition(id: string): Promise<void> {
    await this.db.execute("UPDATE material_definitions SET archived_at = ? WHERE id = ? AND archived_at IS NULL", [new Date().toISOString(), id]);
  }

  /* ---------------- Products ---------------- */

  async listProducts(): Promise<Product[]> {
    return (await this.db.select<ProductRow>("SELECT * FROM products ORDER BY name")).map(mapProduct);
  }

  async listProductsForDefinition(definitionId: string): Promise<Product[]> {
    return (await this.db.select<ProductRow>("SELECT * FROM products WHERE material_definition_id = ? ORDER BY name", [definitionId])).map(mapProduct);
  }

  async getProduct(id: string): Promise<Product | null> {
    const rows = await this.db.select<ProductRow>("SELECT * FROM products WHERE id = ?", [id]);
    return rows.length > 0 ? mapProduct(rows[0]) : null;
  }

  async createProduct(input: ProductInput): Promise<Product> {
    const problems = validateProduct(input);
    if (problems.length > 0) throw new ValidationError(problems);
    const now = new Date().toISOString();
    const p: Product = { id: generateId(), materialDefinitionId: input.materialDefinitionId, name: input.name.trim(), brand: input.brand.trim(), sku: input.sku.trim(), supplierId: input.supplierId, purchaseUnit: input.purchaseUnit, coveragePerUnitX100: input.coveragePerUnitX100, coverageUnit: input.coverageUnit, productUrl: input.productUrl.trim(), status: "active", notes: input.notes, createdAt: now, updatedAt: now };
    await this.db.execute(
      `INSERT INTO products (id, material_definition_id, name, brand, sku, supplier_id, purchase_unit, coverage_per_unit_x100, coverage_unit, product_url, status, notes, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 'active', ?, ?, ?)`,
      [p.id, p.materialDefinitionId, p.name, p.brand, p.sku, p.supplierId, p.purchaseUnit, p.coveragePerUnitX100, p.coverageUnit, p.productUrl, p.notes, p.createdAt, p.updatedAt],
    );
    return p;
  }

  /** Catalog edits never touch project snapshots or purchase history. */
  async updateProduct(id: string, input: ProductInput): Promise<void> {
    const problems = validateProduct(input);
    if (problems.length > 0) throw new ValidationError(problems);
    await this.db.execute(
      `UPDATE products SET material_definition_id = ?, name = ?, brand = ?, sku = ?, supplier_id = ?, purchase_unit = ?,
         coverage_per_unit_x100 = ?, coverage_unit = ?, product_url = ?, notes = ?, updated_at = ? WHERE id = ?`,
      [input.materialDefinitionId, input.name.trim(), input.brand.trim(), input.sku.trim(), input.supplierId, input.purchaseUnit, input.coveragePerUnitX100, input.coverageUnit, input.productUrl.trim(), input.notes, new Date().toISOString(), id],
    );
  }

  /** Lifecycle: active → discontinued (warn on new selection) → archived (hidden). Never deleted. */
  async setProductStatus(id: string, status: ProductStatus): Promise<void> {
    await this.db.execute("UPDATE products SET status = ?, updated_at = ? WHERE id = ?", [status, new Date().toISOString(), id]);
  }

  async listAttributes(productId: string): Promise<ProductAttribute[]> {
    const rows = await this.db.select<{ product_id: string; key: string; value: string; sort_order: number }>(
      "SELECT * FROM product_attributes WHERE product_id = ? ORDER BY sort_order, key", [productId]);
    return rows.map((r) => ({ productId: r.product_id, key: r.key, value: r.value, sortOrder: Number(r.sort_order) }));
  }

  async setAttribute(productId: string, key: string, value: string): Promise<void> {
    const k = key.trim();
    if (k === "") throw new ValidationError(["Attribute key is required."]);
    const existing = await this.listAttributes(productId);
    const sort = existing.find((a) => a.key === k)?.sortOrder ?? existing.reduce((m, a) => Math.max(m, a.sortOrder), 0) + 10;
    await this.db.execute(
      "INSERT INTO product_attributes (product_id, key, value, sort_order) VALUES (?, ?, ?, ?) ON CONFLICT(product_id, key) DO UPDATE SET value = excluded.value",
      [productId, k, value.trim(), sort]);
  }

  async removeAttribute(productId: string, key: string): Promise<void> {
    await this.db.execute("DELETE FROM product_attributes WHERE product_id = ? AND key = ?", [productId, key]);
  }

  /** Search products by name/brand/SKU/attribute value (case-insensitive substring). */
  async searchProducts(query: string): Promise<Product[]> {
    const q = `%${query.trim().toLowerCase()}%`;
    const rows = await this.db.select<ProductRow>(
      `SELECT DISTINCT p.* FROM products p
         LEFT JOIN product_attributes a ON a.product_id = p.id
        WHERE lower(p.name) LIKE ? OR lower(p.brand) LIKE ? OR lower(p.sku) LIKE ? OR lower(a.value) LIKE ? OR lower(a.key) LIKE ?
        ORDER BY p.name`, [q, q, q, q, q]);
    return rows.map(mapProduct);
  }

  /* ---------------- Preferences (relationship; org-scopable later) ---------------- */

  async listPreferred(definitionId: string): Promise<string[]> {
    const rows = await this.db.select<{ product_id: string }>(
      "SELECT product_id FROM material_preferred_products WHERE material_definition_id = ? ORDER BY sort_order", [definitionId]);
    return rows.map((r) => r.product_id);
  }

  async setPreferred(definitionId: string, productId: string, preferred: boolean): Promise<void> {
    if (preferred) {
      await this.db.execute(
        "INSERT OR IGNORE INTO material_preferred_products (material_definition_id, product_id, label, sort_order, created_at) VALUES (?, ?, 'Preferred', 0, ?)",
        [definitionId, productId, new Date().toISOString()]);
    } else {
      await this.db.execute("DELETE FROM material_preferred_products WHERE material_definition_id = ? AND product_id = ?", [definitionId, productId]);
    }
  }

  /* ---------------- Price observations (append-only) ---------------- */

  async listObservations(productId: string): Promise<PriceObservation[]> {
    return (await this.db.select<ObservationRow>(// rowid breaks a tie when two observations share a timestamp, so "the
      // newest offer" is deterministic rather than insertion-order luck.
      "SELECT * FROM price_observations WHERE product_id = ? ORDER BY observed_at DESC, created_at DESC, rowid DESC", [productId])).map(mapObservation);
  }

  async getObservation(id: string): Promise<PriceObservation | null> {
    const rows = await this.db.select<ObservationRow>("SELECT * FROM price_observations WHERE id = ?", [id]);
    return rows.length > 0 ? mapObservation(rows[0]) : null;
  }

  async addObservation(productId: string, input: ObservationInput): Promise<PriceObservation> {
    const problems = validateObservation(input);
    if (problems.length > 0) throw new ValidationError(problems);
    const o: PriceObservation = { id: generateId(), productId, supplierId: input.supplierId, priceCents: input.priceCents, priceIncludesTax: input.priceIncludesTax, sourceType: input.sourceType, observedAt: input.observedAt, validUntil: input.validUntil, contextNote: input.contextNote, notes: input.notes, marketZip: input.marketZip ?? null, availability: input.availability ?? "unknown", offerUrl: (input.offerUrl ?? "").trim(), supplierSku: (input.supplierSku ?? "").trim(), providerId: input.providerId ?? null, createdAt: new Date().toISOString() };
    await this.db.execute(
      `INSERT INTO price_observations (id, product_id, supplier_id, price_cents, price_includes_tax, source_type, observed_at, valid_until, context_note, notes, market_zip, availability, offer_url, supplier_sku, provider_id, created_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [o.id, o.productId, o.supplierId, o.priceCents, o.priceIncludesTax ? 1 : 0, o.sourceType, o.observedAt, o.validUntil, o.contextNote, o.notes, o.marketZip, o.availability, o.offerUrl, o.supplierSku, o.providerId, o.createdAt]);
    return o;
  }

  async removeObservation(id: string): Promise<void> {
    const refs = await this.db.select<{ c: number }>("SELECT COUNT(*) c FROM project_product_selections WHERE price_observation_id = ?", [id]);
    if (Number(refs[0].c) > 0) throw new ValidationError(["This price is referenced by a project selection snapshot; it cannot be removed."]);
    await this.db.execute("DELETE FROM price_observations WHERE id = ?", [id]);
  }

  /* ---------------- Project product selections (copy-on-use) ---------------- */

  async listSelectionsForProject(projectId: string): Promise<ProjectProductSelection[]> {
    const rows = await this.db.select<SelectionRow>(
      `SELECT s.* FROM project_product_selections s
         JOIN assembly_instance_components c ON c.id = s.component_id
         JOIN assembly_instances i ON i.id = c.instance_id
         JOIN project_features f ON f.id = i.project_feature_id
        WHERE f.project_id = ? AND s.replaced_at IS NULL`, [projectId]);
    return rows.map(mapSelection);
  }

  async getActiveSelection(componentId: string): Promise<ProjectProductSelection | null> {
    const rows = await this.db.select<SelectionRow>(
      "SELECT * FROM project_product_selections WHERE component_id = ? AND replaced_at IS NULL ORDER BY created_at DESC LIMIT 1", [componentId]);
    return rows.length > 0 ? mapSelection(rows[0]) : null;
  }

  async listSelectionHistory(componentId: string): Promise<ProjectProductSelection[]> {
    return (await this.db.select<SelectionRow>("SELECT * FROM project_product_selections WHERE component_id = ? ORDER BY created_at DESC", [componentId])).map(mapSelection);
  }

  /**
   * Select a product (+ optional price observation) for a component:
   * supersedes the current selection (kept with replaced_at) and writes a
   * full snapshot. Archived products are refused; discontinued ones require
   * explicit acknowledgement.
   */
  async selectProduct(componentId: string, productId: string, priceObservationId: string | null, options: { allowDiscontinued?: boolean; roundSeparately?: boolean; purchaseQtyOverride?: number | null } = {}): Promise<ProjectProductSelection> {
    const product = await this.getProduct(productId);
    if (!product) throw new ValidationError(["The product no longer exists."]);
    if (product.status === "archived") throw new ValidationError(["This product is archived and cannot be selected for new work."]);
    if (product.status === "discontinued" && !options.allowDiscontinued) {
      throw new ValidationError(["This product is discontinued. Confirm explicitly to select it anyway."]);
    }
    const obs = priceObservationId ? await this.getObservation(priceObservationId) : null;
    if (priceObservationId && !obs) throw new ValidationError(["The chosen price no longer exists."]);
    if (obs && obs.productId !== productId) throw new ValidationError(["The chosen price belongs to a different product."]);
    const supplierId = obs?.supplierId ?? product.supplierId;
    const supplierName = supplierId ? ((await this.db.select<{ name: string }>("SELECT name FROM suppliers WHERE id = ?", [supplierId]))[0]?.name ?? "") : "";
    const prev = await this.getActiveSelection(componentId);
    const now = new Date().toISOString();
    if (prev) await this.db.execute("UPDATE project_product_selections SET replaced_at = ? WHERE id = ?", [now, prev.id]);
    const sel: ProjectProductSelection = {
      id: generateId(), componentId, productId, priceObservationId: obs?.id ?? null,
      productNameSnapshot: product.name, supplierNameSnapshot: supplierName,
      purchaseUnitSnapshot: product.purchaseUnit, coveragePerUnitX100Snapshot: product.coveragePerUnitX100, coverageUnitSnapshot: product.coverageUnit,
      priceCentsSnapshot: obs?.priceCents ?? 0, priceIncludesTaxSnapshot: obs?.priceIncludesTax ?? false,
      priceSourceSnapshot: obs?.sourceType ?? "manual", priceObservedAtSnapshot: obs?.observedAt ?? null,
      roundSeparately: options.roundSeparately ?? prev?.roundSeparately ?? false,
      purchaseQtyOverride: options.purchaseQtyOverride === undefined ? (prev?.purchaseQtyOverride ?? null) : options.purchaseQtyOverride,
      createdAt: now, replacedAt: null,
    };
    await this.db.execute(
      `INSERT INTO project_product_selections (id, component_id, product_id, price_observation_id, product_name_snapshot, supplier_name_snapshot,
         purchase_unit_snapshot, coverage_per_unit_x100_snap, coverage_unit_snapshot, price_cents_snapshot, price_includes_tax_snapshot,
         price_source_snapshot, price_observed_at_snapshot, round_separately, purchase_qty_override, created_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [sel.id, sel.componentId, sel.productId, sel.priceObservationId, sel.productNameSnapshot, sel.supplierNameSnapshot, sel.purchaseUnitSnapshot, sel.coveragePerUnitX100Snapshot, sel.coverageUnitSnapshot, sel.priceCentsSnapshot, sel.priceIncludesTaxSnapshot ? 1 : 0, sel.priceSourceSnapshot, sel.priceObservedAtSnapshot, sel.roundSeparately ? 1 : 0, sel.purchaseQtyOverride, sel.createdAt]);
    return sel;
  }

  /** Settings that don't change the commercial snapshot. */
  async updateSelectionSettings(selectionId: string, settings: { roundSeparately?: boolean; purchaseQtyOverride?: number | null }): Promise<void> {
    if (settings.purchaseQtyOverride !== undefined && settings.purchaseQtyOverride !== null && (!Number.isSafeInteger(settings.purchaseQtyOverride) || settings.purchaseQtyOverride < 0)) {
      throw new ValidationError(["Purchase quantity override must be a whole non-negative number."]);
    }
    if (settings.roundSeparately !== undefined) {
      await this.db.execute("UPDATE project_product_selections SET round_separately = ? WHERE id = ?", [settings.roundSeparately ? 1 : 0, selectionId]);
    }
    if (settings.purchaseQtyOverride !== undefined) {
      await this.db.execute("UPDATE project_product_selections SET purchase_qty_override = ? WHERE id = ?", [settings.purchaseQtyOverride, selectionId]);
    }
  }

  /** Remove the product selection (component returns to Phase 4 estimating cost). History kept. */
  async clearSelection(componentId: string): Promise<void> {
    await this.db.execute("UPDATE project_product_selections SET replaced_at = ? WHERE component_id = ? AND replaced_at IS NULL", [new Date().toISOString(), componentId]);
  }

  /** Explicit "update from catalog" re-snapshots the same product with a chosen observation. */
  async updateSelectionFromCatalog(componentId: string, priceObservationId: string | null): Promise<ProjectProductSelection> {
    const current = await this.getActiveSelection(componentId);
    if (!current) throw new ValidationError(["No product is selected for this component."]);
    return this.selectProduct(componentId, current.productId, priceObservationId, { allowDiscontinued: true });
  }

  /* ---------------- Purchase history ---------------- */

  /** Purchase lines linked to a product (for Recent Purchases / historical-paid). */
  async listPurchaseLinesForProduct(productId: string): Promise<Array<{ purchaseId: string; lineId: string; vendor: string; date: string; quantityX100: number | null; unit: string | null; voided: boolean }>> {
    const rows = await this.db.select<{ purchase_id: string; line_id: string; vendor: string; date: string; quantity_x100: number | null; unit: string | null; status: string }>(
      `SELECT p.id AS purchase_id, l.id AS line_id, p.vendor, p.date, l.quantity_x100, l.unit, p.status
         FROM purchase_lines l JOIN purchases p ON p.id = l.purchase_id
        WHERE l.product_id = ? ORDER BY p.date DESC`, [productId]);
    return rows.map((r) => ({ purchaseId: r.purchase_id, lineId: r.line_id, vendor: r.vendor, date: r.date, quantityX100: r.quantity_x100 === null ? null : Number(r.quantity_x100), unit: r.unit, voided: r.status === "voided" }));
  }

  async setPurchaseLineProduct(lineId: string, productId: string | null): Promise<void> {
    if (productId) {
      const p = await this.getProduct(productId);
      if (!p) throw new ValidationError(["The product no longer exists."]);
    }
    await this.db.execute("UPDATE purchase_lines SET product_id = ? WHERE id = ?", [productId, lineId]);
  }
}
